import axios from 'axios';
import CONFIG, { getApiUrl } from '../../../utils/config';

interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  result: string;
  message: string;
  data?: {
    token: string;
    tokenType: string;
    expiresIn: number;
    refreshToken: string;
    refreshTokenExpiresIn: number;
    user: {
      id: number;
      username: string;
      email: string;
      firstName: string;
      lastName: string;
      profileImageUrl: string | null;
      gender: string | null;
      role: string;
      active: boolean;
      stravaAthleteId: number | null;
      hasStravaToken: boolean;
      createdAt: any;
      updatedAt: any;
    };
  };
}

interface RegisterRequestNew {
  username: string;
  password: string;
  email: string;
  firstName: string;
  lastName: string;
  gender: 'MALE' | 'FEMALE' | 'OTHER';
}

interface RegisterResponse {
  id?: number;
  username?: string;
  message?: string;
}

interface StravaAuthResponse {
  result: string;
  message: string;
  data: string; // URL xác thực Strava
}

interface StravaCallbackResponse {
  result: string;
  message: string;
  data?: {
    token: string;
    tokenType: string;
    expiresIn: number;
    refreshToken: string;
    refreshTokenExpiresIn: number;
    user: {
      id: number;
      username: string;
      email: string;
      firstName: string;
      lastName: string;
      profileImageUrl: string | null;
      gender: string | null;
      role: string;
      active: boolean;
      stravaAthleteId: number | null;
      hasStravaToken: boolean;
      createdAt: any;
      updatedAt: any;
    };
  };
}

/**
 * Đăng nhập và lưu token vào localStorage
 * @param credentials Thông tin đăng nhập
 * @returns Kết quả đăng nhập
 */
export const loginAndSaveToken = async (
  credentials: LoginRequest
): Promise<LoginResponse> => {
  try {
    console.log('Đang đăng nhập với:', credentials.username);
    const response = await fetch(getApiUrl(CONFIG.ENDPOINTS.AUTH.LOGIN), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    });

    const data = await response.json();
    console.log('Kết quả đăng nhập:', data);
    
    if (!response.ok) {
      return {
        result: 'ERROR',
        message: data.message || 'Đăng nhập thất bại',
      };
    }
    
    // Lưu token vào localStorage
    if (data.data?.token) {
      // Lưu token theo cấu trúc cũ
      localStorage.setItem('token', data.data.token);
      localStorage.setItem('tokenType', data.data.tokenType);
      localStorage.setItem('expiresIn', data.data.expiresIn.toString());
      
      // Lưu refresh token
      localStorage.setItem('refreshToken', data.data.refreshToken);
      localStorage.setItem('refreshTokenExpiresIn', data.data.refreshTokenExpiresIn.toString());
      
      localStorage.setItem('user', JSON.stringify(data.data.user));
      
      // Lưu token theo định dạng mới cho authService
      localStorage.setItem('jwt_token', data.data.token);
      localStorage.setItem('auth_type', data.data.tokenType);
      localStorage.setItem('auth_user_id', data.data.user.id.toString());
      localStorage.setItem('auth_username', data.data.user.username);
      
      console.log('Đã lưu token vào localStorage:', {
        jwt_token: data.data.token.substring(0, 10) + '...',
        refreshToken: data.data.refreshToken.substring(0, 10) + '...',
        username: data.data.user.username,
        userId: data.data.user.id
      });
    }
    
    return data;
  } catch (error: any) {
    console.error('Lỗi khi đăng nhập:', error);
    return {
      result: 'ERROR',
      message: 'Đã xảy ra lỗi khi kết nối đến máy chủ',
    };
  }
};

/**
 * Đăng ký tài khoản mới sử dụng API mới
 * @param userData Thông tin người dùng đăng ký
 * @returns Kết quả đăng ký
 */
export const registerServiceNew = async (
  userData: RegisterRequestNew
): Promise<RegisterResponse> => {
  try {
    const response = await fetch(getApiUrl(CONFIG.ENDPOINTS.AUTH.REGISTER), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    });

    const data = await response.json();
    
    if (!response.ok) {
      return {
        message: data.message || 'Đăng ký thất bại',
      };
    }
    
    return data;
  } catch (error: any) {
    return {
      message: 'Đã xảy ra lỗi khi kết nối đến máy chủ',
    };
  }
};

/**
 * Lấy URL xác thực từ Strava
 * @returns Kết quả chứa URL để chuyển hướng đến trang xác thực Strava
 */
export const stravaAuth = async (): Promise<StravaAuthResponse> => {
  try {
    const response = await fetch(getApiUrl(CONFIG.ENDPOINTS.AUTH.STRAVA_AUTH), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    const data = await response.json();
    
    if (!response.ok) {
      return {
        result: 'ERROR',
        message: data.message || 'Không thể kết nối với Strava',
        data: '',
      };
    }
    
    return data;
  } catch (error: any) {
    return {
      result: 'ERROR',
      message: 'Đã xảy ra lỗi khi kết nối đến máy chủ',
      data: '',
    };
  }
};

/**
 * Xử lý callback từ Strava sau khi xác thực
 * @param code Mã xác thực từ Strava
 * @param userId ID của người dùng (tùy chọn)
 * @returns Kết quả xác thực
 */
export const stravaCallback = async (
  code: string,
  userId?: string | number
): Promise<StravaCallbackResponse> => {
  try {
    // Tạo URL với code và thêm userId nếu có
    let url = `${getApiUrl(CONFIG.ENDPOINTS.AUTH.STRAVA_CALLBACK)}?code=${code}`;
    if (userId) {
      url += `&userId=${userId}`;
    }
    
    console.log('Gọi API callback Strava:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    const data = await response.json();
    console.log('Kết quả callback Strava:', data);
    
    if (!response.ok) {
      return {
        result: 'ERROR',
        message: data.message || 'Không thể xác thực với Strava',
      };
    }
    
    // Lưu token vào localStorage nếu có
    if (data.data?.token) {
      // Lưu token theo cấu trúc cũ
      localStorage.setItem('token', data.data.token);
      localStorage.setItem('tokenType', data.data.tokenType);
      localStorage.setItem('expiresIn', data.data.expiresIn.toString());
      
      // Lưu refresh token
      localStorage.setItem('refreshToken', data.data.refreshToken);
      localStorage.setItem('refreshTokenExpiresIn', data.data.refreshTokenExpiresIn.toString());
      
      localStorage.setItem('user', JSON.stringify(data.data.user));
      
      // Lưu token theo định dạng mới cho authService
      localStorage.setItem('jwt_token', data.data.token);
      localStorage.setItem('auth_type', data.data.tokenType);
      localStorage.setItem('auth_user_id', data.data.user.id.toString());
      localStorage.setItem('auth_username', data.data.user.username);
      
      console.log('Đã lưu token vào localStorage từ Strava callback:', {
        jwt_token: data.data.token.substring(0, 10) + '...',
        refreshToken: data.data.refreshToken.substring(0, 10) + '...',
        username: data.data.user.username,
        userId: data.data.user.id
      });
    }
    
    return data;
  } catch (error: any) {
    console.error('Lỗi khi xử lý callback Strava:', error);
    return {
      result: 'ERROR',
      message: 'Đã xảy ra lỗi khi kết nối đến máy chủ',
    };
  }
};

export default {
  loginAndSaveToken,
  registerServiceNew,
  stravaAuth,
  stravaCallback
};
